import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  message,
  Statistic,
  Row,
  Col,
  Tag,
  Descriptions,
  Empty,
  Space,
  Alert
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { PageHeader } from '../components/PageHeader';
import { api, EntryAllocationsResponse } from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Text } = Typography;

const AdminProfitAllocations = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EntryAllocationsResponse | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  useEffect(() => {
    if (id) {
      fetchAllocations();
    }
  }, [id]);

  const fetchAllocations = async (page = 1) => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await api.profitEntries.getEntryAllocations(id, {
        page,
        per: pagination.pageSize,
      });
      
      setData(response);
      setPagination({
        current: page,
        pageSize: response.pagination.per_page,
        total: response.pagination.total_count,
      });
    } catch (error: any) {
      console.error('Failed to fetch allocations:', error);
      message.error('Failed to load allocations');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    fetchAllocations(newPagination.current);
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_: any, record: any) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{record.user.name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.user.email}</Text>
        </Space>
      ),
      width: 250,
    },
    {
      title: 'Ownership %',
      dataIndex: 'ownership_percent',
      key: 'ownership_percent',
      render: (percent: string) => `${Number(percent).toFixed(4)}%`,
      width: 130,
      align: 'right' as const,
    },
    {
      title: 'Gross Share',
      dataIndex: 'gross_share_cents',
      key: 'gross_share',
      render: (cents: number) => (
        <Text style={{ color: '#1890ff' }}>
          {formatCurrency(cents)}
        </Text>
      ),
      width: 130,
      align: 'right' as const,
    },
    {
      title: 'Fees',
      key: 'company_cut',
      width: 150,
      render: (_: any, record: any) => (
        <Space orientation="vertical" size={0}>
          <Text>{(Number(record.company_cut_percent) * 100).toFixed(2)}%</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formatCurrency(record.company_cut_cents)}
          </Text>
        </Space>
      ),
      align: 'right' as const,
    },
    {
      title: 'Net Share',
      dataIndex: 'net_share_cents',
      key: 'net_share',
      render: (cents: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatCurrency(cents)}
        </Text>
      ),
      width: 130,
      align: 'right' as const,
    },
  ];

  if (!data && !loading) {
    return (
      <>
        <PageHeader
          title="Profit Allocations"
          description="Allocation details not found"
        />
        <Card>
          <Empty description="Unable to load allocation details" />
        </Card>
      </>
    );
  }

  const profitEntry = data?.profit_entry;
  const allocations = data?.allocations || [];
  const totals = data?.totals;

  // Reconciliation check
  const reconciled = totals 
    ? (totals.gross_share_cents === (totals.net_share_cents + totals.company_cut_cents))
    : false;

  return (
    <>
      <PageHeader
        title="Profit Allocations"
        description="Detailed breakdown of profit distribution"
        extra={
          <Tag color="green" icon={<CheckCircleOutlined />}>
            DISTRIBUTED
          </Tag>
        }
      />

      {/* Back Button */}
      <Card style={{ marginBottom: 16 }}>
        <a
          onClick={() => navigate('/admin/profit-entry')}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <ArrowLeftOutlined />
          <span>Back to Profit Entries</span>
        </a>
      </Card>

      {/* Profit Entry Details */}
      {profitEntry && (
        <Card title="Profit Entry Details" style={{ marginBottom: 16 }}>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Entry ID">
              <Text copyable>{profitEntry.id.substring(0, 8)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Entry Date">
              {dayjs(profitEntry.entry_date).format('MMM D, YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {dayjs(profitEntry.end_date).format('MMM D, YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Gross Profit">
              <Text strong>{formatCurrency(profitEntry.gross_profit_cents)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Entered By">
              {profitEntry.entered_by.name}
            </Descriptions.Item>
            {profitEntry.notes && (
              <Descriptions.Item label="Notes" span={3}>
                {profitEntry.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* Totals Summary */}
      {totals && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Gross Share"
                  value={totals.gross_share_cents / 100}
                  precision={2}
                  prefix={<DollarOutlined />}
                  styles={{ content: { color: '#1890ff' } }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Fees"
                  value={totals.company_cut_cents / 100}
                  precision={2}
                  prefix={<DollarOutlined />}
                  styles={{ content: { color: '#faad14' } }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Net Share"
                  value={totals.net_share_cents / 100}
                  precision={2}
                  prefix={<DollarOutlined />}
                  styles={{ content: { color: '#52c41a' } }}
                />
              </Card>
            </Col>
          </Row>

          {/* Reconciliation Alert */}
          <Alert
            type={reconciled ? 'success' : 'error'}
            title={
              reconciled
                ? 'Reconciliation Check Passed'
                : 'Reconciliation Check Failed'
            }
            description={
              reconciled
                ? `Gross Share (${formatCurrency(totals.gross_share_cents)}) = Net Share (${formatCurrency(totals.net_share_cents)}) + Fees (${formatCurrency(totals.company_cut_cents)})`
                : `Mismatch detected: Gross Share (${formatCurrency(totals.gross_share_cents)}) ≠ Net Share (${formatCurrency(totals.net_share_cents)}) + Fees (${formatCurrency(totals.company_cut_cents)})`
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        </>
      )}

      {/* Allocations Table */}
      <Card title={`User Allocations (${pagination.total} users)`}>
        <Table
          columns={columns}
          dataSource={allocations}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          locale={{
            emptyText: (
              <Empty
                description="No allocations found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>
    </>
  );
};

export default AdminProfitAllocations;
